import { show as showNiceModal, show } from '@ebay/nice-modal-react'
import {
  Assignment,
  InsertChart,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreHoriz,
  Settings,
  ShoppingCart,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import { CircularProgress, LinearProgress, useMediaQuery } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ReactElement, RefObject, useMemo, useRef, useState } from 'react'
import { I18n, MY_PLACE_SELLER, Translate2 } from 'common'
import { MultiPartInquiryModel } from '../../../../../src/store/models/SearchModels'
import { useSearchStore } from '../../../../../src/store/search/SearchStore'
import { useUiStateStore } from '../../../../../src/store/uiState/UiStateStore'
import { defaultTheme } from '../../../../../src/theme/index'
import styled from 'styled-components'
import { CondensedViewSettingsDrawer } from './CondensedViewSettingsDrawer'
import { useResultsStore } from '../../store/ResultsStore'
import { SortDropdown } from './SortDropdown'
import miscellaneousVehicle from '../../../../../src/features/search/Results/utils/miscellaneousVehicle'
import ExtraDetailsDrawer from './ExtraDetailsDrawer'
import { VehicleSpecificationDrawer } from '../../../../../src/features/search/VehicleSearch/components/VehicleSpecificationDrawer'
import { usePartsCatalog } from '../../../../../src/store/partsCatalog/PartsCatalog'
import { CatalogNodeType } from '../../../../../src/store/partsCatalog'
import {
  checkAreAllCartsEmpty,
  isThereOnlyTheMiscellaneousCart,
  setQtyAtLocationBulk,
} from 'cart'
import { hawkSearchProvider } from '../../../../../src/config/analytics/core/providers/HawkSearchServiceProvider'
import { prepareEventData } from '../../../../../src/helpers/HawkSearchEventHelpers'
import { RootState } from 'store'
import { useSelector } from 'react-redux'
import ShowNotification from '../../../../../src/ui-components/modals/ToastNotifcationNotiStack'
import { ToastNotificationVariant } from '../../../../../src/ui-components/util/Interfaces'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { getAppliedFilterCount } from '../../../../../src/features/filterResults/getAppliedFilterCount'
import { FilterSortButton } from './resultsBar/FilterSortButton'
import { ResultsBarButton } from './resultsBar/ResultsBarButton'
import { ResultsCountLabel } from './resultsBar/ResultsCountLabel'
import { getResultsSearchTerm } from './resultsBar/getResultsSearchTerm'

interface Props {
  totalResults: number
  onClickFilters: () => void
}

function isValidMultiInquiryPart(
  multiPartInquiryModel: MultiPartInquiryModel,
  orderIfNotAvailable: boolean
): boolean {
  if (orderIfNotAvailable) {
    return true
  }

  const { qty, activeLocation } = multiPartInquiryModel
  return activeLocation?.qtyAvailable >= qty
}

const ResultsContentHeader = observer((props: Props): ReactElement => {
  const { totalResults, onClickFilters } = props
  const searchStore = useSearchStore()
  const uiStore = useUiStateStore()
  const resultsStore = useResultsStore()
  const partsCatalog = usePartsCatalog()
  const {
    showAddAlltoCart,
    currentVehicle,
    setShowAddAlltoCart,
    setCartSelectorModal,
  } = searchStore

  const {
    isLoading,
    multiPartInquiryResults,
    filters,
    selectedLinesManufacturers,
  } = resultsStore

  const [showSortBox, setShowSortBox] = useState(false)
  const [isAddingAllToCart, setIsAddingAllToCart] = useState(false)
  const sortButtonRef = useRef<HTMLDivElement>(null)

  const isMobile = useMediaQuery('(max-width:767px)')
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1279px)')
  const isDesktop = useMediaQuery('(min-width:1280px)')
  const showCollapsedSidebar = useMediaQuery('(max-width:1460px)')

  const searchTerm = getResultsSearchTerm(searchStore)

  const filterCount = useMemo(
    () => getAppliedFilterCount(filters, selectedLinesManufacturers),
    [filters, selectedLinesManufacturers]
  )

  const showDiagrams =
    import.meta.env.VITE_FEATURE_CATALOG_DIAGRAM_ENABLED === 'true' &&
    (resultsStore?.diagrams?.parts ?? []).length > 0

  const showSpecifications = !!(
    currentVehicle?.engine?.id && currentVehicle?.type?.id === 111
  )

  const showOverflowMenu = isMobile || isTablet

  const cartData = useSelector((state: RootState) => state.cart.cartData)

  const orderIfNotAvailable =
    StoreInstances.userStore?.preferences?.findit_orderIfNotAvail === 'true'

  const handleAddAllPartsToCart = async (
    multiPartInquiryModels: MultiPartInquiryModel[]
  ) => {
    const vehicleToAdd = currentVehicle?.engine
      ? currentVehicle
      : miscellaneousVehicle
    await setQtyAtLocationBulk(
      multiPartInquiryModels.map((multiPartInquiryModel) => ({
        product: multiPartInquiryModel.part,
        locationId: multiPartInquiryModel?.activeLocation?.locationId,
        quantity: multiPartInquiryModel.qty,
        vehicle: vehicleToAdd,
        seller: MY_PLACE_SELLER,
      })),
      false
    )
    const seen = new Set<number>()
    multiPartInquiryModels.forEach((model) => {
      if (seen.has(model.part.lineNo)) return
      seen.add(model.part.lineNo)
      hawkSearchProvider.trackEvent(
        'Add2Cart',
        prepareEventData('Add2Cart', {
          productId:
            model.part.allianceProductId ||
            `${model.part.lineCode} ${model.part.partNumber}`,
          price: model.activeLocation?.cost,
          quantity: model.qty,
          vehicle: vehicleToAdd,
        })
      )
    })
  }

  const handleAddAllToCartClick = async (): Promise<void> => {
    if (isAddingAllToCart) return
    if (!isThereOnlyTheMiscellaneousCart()) {
      setCartSelectorModal(true)
      return
    }

    const items: MultiPartInquiryModel[] = []
    multiPartInquiryResults.forEach((item) => {
      if (
        item.activeLocation.locType !== 'viewOnly' &&
        item.activeLocation?.cost !== 0 &&
        isValidMultiInquiryPart(item, orderIfNotAvailable)
      ) {
        items.push(item)
      }
    })

    try {
      setIsAddingAllToCart(true)
      await handleAddAllPartsToCart(items)
      setShowAddAlltoCart(false)
      if (!checkAreAllCartsEmpty(cartData)) {
        ShowNotification({
          message: 'partsHaveBeenAddedToYourCart',
          variant: ToastNotificationVariant.SUCCESS,
        })
      }
    } finally {
      setIsAddingAllToCart(false)
    }
  }

  const handleCondensedViewSettings = (): void => {
    showNiceModal(CondensedViewSettingsDrawer)
  }

  const handleShowDiagrams = (): void => {
    uiStore.setShowPartsCatalogDiagramDrawer(true)
  }

  const handleShowSpecifications = (): void => {
    const storeGroupsSelected = partsCatalog.root.getAllDescendants(
      (node) => node.type === CatalogNodeType.GROUP && node.selected
    )
    show(VehicleSpecificationDrawer, { storeGroupsSelected })
  }

  const handleOverflowClick = (): void => {
    showNiceModal(ExtraDetailsDrawer)
  }

  const yourCostButton = (
    <ResultsBarButton onClick={() => uiStore.updateYourCost()}>
      {uiStore.showYourCost() ? <VisibilityIcon /> : <VisibilityOffIcon />}
      <I18n name="yourCost" />
    </ResultsBarButton>
  )

  const settingsButton = (
    <ResultsBarButton
      onClick={handleCondensedViewSettings}
      aria-label={Translate2('resultsViewSettings')}
      useDefaultIconColor
    >
      <Settings />
    </ResultsBarButton>
  )

  const sortButton = (
    <SortButtonWrapper ref={sortButtonRef}>
      <ResultsBarButton onClick={() => setShowSortBox(!showSortBox)}>
        <I18n name="sort" />
        {showSortBox ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </ResultsBarButton>
      <SortDropdown
        parentRef={sortButtonRef as RefObject<HTMLElement>}
        show={showSortBox}
        setShow={() => setShowSortBox(false)}
      />
    </SortButtonWrapper>
  )

  const addAllToCartButton = showAddAlltoCart && (
    <ResultsBarButton
      onClick={handleAddAllToCartClick}
      disabled={isAddingAllToCart}
    >
      {isAddingAllToCart ? <CircularProgress size={18} /> : <ShoppingCart />}
      <I18n name="addAllToCart" />
    </ResultsBarButton>
  )

  const desktopActions = (
    <ActionsRow>
      {showDiagrams && (
        <ResultsBarButton onClick={handleShowDiagrams}>
          <InsertChart />
          <I18n name="diagrams" />
        </ResultsBarButton>
      )}
      {addAllToCartButton}
      {showSpecifications && (
        <ResultsBarButton onClick={handleShowSpecifications}>
          <Assignment />
          <I18n name="specifications" />
        </ResultsBarButton>
      )}
      {yourCostButton}
      {showCollapsedSidebar ? (
        <FilterSortButton filterCount={filterCount} onClick={onClickFilters} />
      ) : (
        sortButton
      )}
      {settingsButton}
    </ActionsRow>
  )

  const compactActionContent = (
    <>
      {addAllToCartButton}
      {showOverflowMenu && (showDiagrams || showSpecifications) && (
        <ResultsBarButton
          onClick={handleOverflowClick}
          aria-label={Translate2('moreResultActions')}
          useDefaultIconColor
        >
          <MoreHoriz />
        </ResultsBarButton>
      )}
      {yourCostButton}
      <FilterSortButton filterCount={filterCount} onClick={onClickFilters} />
      {settingsButton}
    </>
  )

  const compactActions = <ActionsRow>{compactActionContent}</ActionsRow>

  const resultsLabel = (
    <ResultsCountLabel
      count={totalResults}
      searchTerm={searchTerm}
      isLoading={isLoading}
    />
  )

  return (
    <Wrapper>
      <Header $isMobile={isMobile}>
        {isDesktop && (
          <DesktopRow>
            {resultsLabel}
            {desktopActions}
          </DesktopRow>
        )}
        {isTablet && (
          <TabletRow>
            {resultsLabel}
            {compactActions}
          </TabletRow>
        )}
        {isMobile && (
          <MobileColumn>
            {resultsLabel}
            <MobileActionsRow>{compactActionContent}</MobileActionsRow>
          </MobileColumn>
        )}
      </Header>
      {isLoading && <LinearProgress />}
    </Wrapper>
  )
})

const Wrapper = styled.div`
  margin: 0 0 14px;
`

interface HeaderProps {
  $isMobile: boolean
}

const Header = styled.div<HeaderProps>`
  background-color: ${defaultTheme.colors.white};
  border-top: 4px solid ${defaultTheme.colors.black};
  position: relative;
  min-height: ${(p) => (p.$isMobile ? 'auto' : '62px')};
`

const DesktopRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  min-height: 62px;
  padding-left: 24px;
`

const TabletRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  min-height: 62px;
  padding-left: 0;
`

const MobileColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
`

const ActionsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex-shrink: 0;
  flex-wrap: wrap;
  height: 100%;
`

const MobileActionsRow = styled(ActionsRow)`
  border-top: 1px solid #d3d2cf;
  width: 100%;
`

const SortButtonWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  height: 100%;
`

export default ResultsContentHeader
